import { useEffect, useMemo, useRef, useState } from 'react';
import { BASEURI, getAuthToken, handleAuthTokenError } from '../../utils';

export default function useLastSubmission({ jobId, includePayload = true, refreshToken } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(Boolean(jobId));
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const url = useMemo(() => {
        if (!jobId) return null;
        const p = new URLSearchParams({ job_id: String(jobId) });
        if (includePayload) p.set('include_payload', 'true');
        return `${BASEURI}/api/dga/submissions/last-submission/?${p.toString()}`;
    }, [jobId, includePayload]);

    const fetchData = async (signal) => {
        if (!url) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const token = await getAuthToken().catch(handleAuthTokenError);
            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    ...(token ? { Authorization: `Bearer  ${token}` } : {}),
                },
                signal,
            });
            if (!res.ok) {
                const msg = await res.text().catch(() => res.statusText);
                throw new Error(msg || `Request failed with status ${res.status}`);
            }
            const json = await res.json();
            setData(json);
        } catch (e) {
            if (signal?.aborted) return;
            setError(e);
            setData(null);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        if (!url) return;
        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        fetchData(ac.signal);
        return () => ac.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, refreshToken]);

    const refetch = () => {
        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        fetchData(ac.signal);
    };

    return { data, loading, error, refetch };
}
